import React, { useMemo, useState } from 'react'
import View from './view.js'
import { useDataChange, useDataFormat, useDataValue } from 'Simple/Data.js'
import produce from 'immer'
import XIcon from 'DesignSystem/Svgs/XIcon'
import { OPERATIONS } from './helpers.js'

import TysiaSelect from 'DesignSystem/TysiaSelect'
import TysiaInput from 'DesignSystem/TysiaInput'
import TysiaSelectContent from 'DesignSystem/TysiaSelect/TysiaSelectContent'
import TysiaSelectField from 'DesignSystem/TysiaSelect/TysiaSelectField/react'
import TysiaInputContent from 'DesignSystem/TysiaInput/TysiaInputContent'
import TysiaInputField from 'DesignSystem/TysiaInput/TysiaInputField'
import TysiaSelectOne from 'DesignSystem/TysiaSelectOne'
import TysiaSelectOneContent from 'DesignSystem/TysiaSelectOne/TysiaSelectOneContent'
import TysiaSelectOneInput from 'DesignSystem/TysiaSelectOne/TysiaSelectOneInput'
import TysiaSelectOneArrow from 'DesignSystem/TysiaSelectOne/TysiaSelectOneArrow'
import TysiaSelectOnePopover from 'DesignSystem/TysiaSelectOne/TysiaSelectOnePopover'
import TysiaSelectOneOptions from 'DesignSystem/TysiaSelectOne/TysiaSelectOneOptions'
import TysiaSelectOneValues from 'design-system/TysiaSelectOne/TysiaSelectOneValues'

export default function Logic(props) {
  let value = useDataValue({
    viewPath: props.viewPath,
    context: 'question',
    path: 'depends_on',
  })
  let question_id = useDataValue({
    viewPath: props.viewPath,
    context: 'question',
    path: 'id',
  })

  let resource_id = useDataValue({
    viewPath: props.viewPath,
    context: 'question',
    path: 'resource_id',
  })

  let change = useDataChange({
    viewPath: props.viewPath,
    context: 'question',
    path: 'depends_on',
  })

  let questions = useDataFormat({
    viewPath: props.viewPath,
    context: 'questions',
    format: array => {
      let questions = array
        .map(({ id, type, prompt, options, resource_id }) => ({
          id: resource_id || id,
          type,
          prompt,
          options,
        }))
        .filter(({ id }) => ![question_id, resource_id].includes(id))

      return questions
    },
  })

  let dataTable = useMemo(() => {
    let columns = [
      {
        header: 'Question',
        accessorKey: 'id',
        cell: cell => (
          <div
            style={{
              width: 240,
            }}
          >
            <TysiaSelect
              allowEmptyChoice={false}
              viewPath={`${props.viewPath}/QuestionSelect`}
              from={questions}
              value={cell.getValue()}
              onChange={newValue => {
                onChangeValue(newValue, cell.column.id, cell.row.index)
              }}
              field="prompt"
              alignItems="stretch"
              textAlign="left"
              marginTop={0}
              minWidth={240}
              maxWidth={500}
              width={240}
              flexGrow={0}
            >
              {childProps => (
                <TysiaSelectContent
                  {...childProps}
                  width={240}
                  viewPath={`${props.viewPath}/TysiaSelectContentQuestion`}
                >
                  {childProps => (
                    <TysiaSelectField
                      {...childProps}
                      viewPath={`${props.viewPath}/TysiaSelectFieldQuestion`}
                    />
                  )}
                </TysiaSelectContent>
              )}
            </TysiaSelect>
          </div>
        ),
      },
      {
        header: 'Criteria',
        accessorKey: 'validation',
        cell: cell => (
          <TysiaSelect
            allowEmptyChoice={false}
            viewPath={`${props.viewPath}/CriteriaSelect`}
            from={OPERATIONS}
            value={cell.getValue()}
            onChange={newValue => {
              onChangeValue(newValue, cell.column.id, cell.row.index)
            }}
            field="text"
            zIndex={1}
            alignItems="stretch"
            textAlign="left"
            marginTop={0}
            minWidth={80}
            maxWidth={100}
            width={100}
            flexGrow={0}
          >
            {childProps => (
              <TysiaSelectContent
                {...childProps}
                viewPath={`${props.viewPath}/TysiaSelectContentCriteria`}
              >
                {childProps => (
                  <TysiaSelectField
                    {...childProps}
                    viewPath={`${props.viewPath}/TysiaSelectFieldCriteria`}
                  />
                )}
              </TysiaSelectContent>
            )}
          </TysiaSelect>
        ),
      },
      {
        header: 'Value',
        accessorKey: 'value',
        cell: ValueCell,
      },
      {
        header: 'Actions',
        id: 'delete',
        accessorFn: _ => 'delete',
        cell: cell => (
          <div
            style={{
              cursor: 'pointer',
              color: 'blue',
              textDecoration: 'underline',
              textAlign: 'center',
            }}
            onClick={() => {
              change(
                value.filter((_, itemIndex) => itemIndex !== cell.row.index)
              )
            }}
          >
            <XIcon
              viewPath={`${props.viewPath}/XIcon`}
              width={14}
              height={14}
              stroke="#000000"
            />
          </div>
        ),
      },
    ]
    return {
      columns,
      data: value,
    }

    function ValueCell(cell) {
      let [inputValue, setInputValue] = useState(cell.getValue())
      let current_question = questions.find(
        question => question.id === cell.row.original.id
      )
      let { type, options } = current_question || {}

      switch (type) {
        case 'boolean': {
          return (
            <TysiaSelect
              allowEmptyChoice={false}
              viewPath={`${props.viewPath}/TysiaSelect${type}`}
              from={[
                {
                  id: true,
                  text: 'Yes',
                },
                {
                  id: false,
                  text: 'No',
                },
              ]}
              value={inputValue}
              onChange={newValue => {
                onChangeValue(newValue, cell.column.id, cell.row.index)
              }}
              zIndex={1}
              alignItems="stretch"
              textAlign="left"
              marginTop={0}
              minWidth={150}
              maxWidth={250}
              width={250}
              flexGrow={0}
            >
              {childProps => (
                <TysiaSelectContent
                  {...childProps}
                  viewPath={`${props.viewPath}/TysiaSelectContent${type}`}
                >
                  {childProps => (
                    <TysiaSelectField
                      {...childProps}
                      viewPath={`${props.viewPath}/TysiaSelectField${type}`}
                    />
                  )}
                </TysiaSelectContent>
              )}
            </TysiaSelect>
          )
        }
        case 'choice': {
          return (
            <TysiaSelect
              allowEmptyChoice={false}
              viewPath={`${props.viewPath}/TysiaSelect${type}`}
              from={options.map(option => ({
                id: option.id || option,
                text: option.text || option,
              }))}
              value={inputValue}
              onChange={newValue => {
                onChangeValue(newValue, cell.column.id, cell.row.index)
              }}
              zIndex={1}
              field="text"
              alignItems="stretch"
              textAlign="left"
              marginTop={0}
              minWidth={150}
              maxWidth={250}
              width={250}
              flexGrow={0}
            >
              {childProps => (
                <TysiaSelectContent
                  {...childProps}
                  viewPath={`${props.viewPath}/TysiaSelectContent${type}`}
                >
                  {childProps => (
                    <TysiaSelectField
                      {...childProps}
                      viewPath={`${props.viewPath}/TysiaSelectField${type}`}
                    />
                  )}
                </TysiaSelectContent>
              )}
            </TysiaSelect>
          )
        }
        case 'multichoice':
        case 'list': {
          return (
            <TysiaSelectOne
              viewPath={`${props.viewPath}/Dropdown${type}`}
              from={options.map(option => ({
                id: option.id || option,
                text: option.text || option,
              }))}
              value={
                Array.isArray(inputValue)
                  ? inputValue
                  : [inputValue].filter(Boolean)
              }
              onChange={newValue => {
                onChangeValue(newValue, cell.column.id, cell.row.index)
              }}
              marginTop={0}
              minWidth={150}
              maxWidth={250}
              width={250}
              flexGrow={0}
            >
              {childProps => (
                <React.Fragment>
                  <TysiaSelectOneContent
                    {...childProps}
                    viewPath={`${props.viewPath}/TysiaSelectOneContent${type}`}
                  >
                    {childProps => (
                      <React.Fragment>
                        <TysiaSelectOneInput
                          {...childProps}
                          viewPath={`${props.viewPath}/TysiaSelectOneInput${type}`}
                        />
                        <TysiaSelectOneArrow
                          {...childProps}
                          viewPath={`${props.viewPath}/TysiaSelectOneArrow${type}`}
                        />
                        <TysiaSelectOnePopover
                          {...childProps}
                          viewPath={`${props.viewPath}/TysiaSelectOnePopover${type}`}
                        >
                          {childProps => (
                            <TysiaSelectOneOptions
                              {...childProps}
                              viewPath={`${props.viewPath}/TysiaSelectOneOptions${type}`}
                            />
                          )}
                        </TysiaSelectOnePopover>
                      </React.Fragment>
                    )}
                  </TysiaSelectOneContent>
                  <TysiaSelectOneValues
                    {...childProps}
                    viewPath={`${props.viewPath}/TysiaSelectOneValues${type}`}
                  />
                </React.Fragment>
              )}
            </TysiaSelectOne>
          )
        }
        default: {
          return (
            <TysiaInput
              viewPath={`${props.viewPath}/TysiaInput`}
              required={true}
              value={inputValue}
              marginTop={0}
              minWidth={150}
              maxWidth={250}
              width={150}
              flexGrow={0}
              onChange={newValue => {
                setInputValue(newValue)
              }}
              onBlur={() => {
                onChangeValue(inputValue, cell.column.id, cell.row.index)
              }}
            >
              {childProps => (
                <TysiaInputContent
                  {...childProps}
                  viewPath={`${props.viewPath}/TysiaInputContent`}
                >
                  {childProps => (
                    <TysiaInputField
                      {...childProps}
                      viewPath={`${props.viewPath}/TysiaInputField`}
                    />
                  )}
                </TysiaInputContent>
              )}
            </TysiaInput>
          )
        }
      }
    }

    function onChangeValue(columnValue, prop, index) {
      let newValue = produce(value, nextValue => {
        if (!Array.isArray(nextValue)) {
          nextValue = []
        }
        nextValue[index][prop] = columnValue
        // If change the question, I reset the condition value
        if (prop === 'id') {
          nextValue[index]['value'] = null
        }
      })

      change(newValue)
    }
  }, [value, change, props.viewPath, questions])

  return <View {...props} dataTable={dataTable} />
}
